import React, { useCallback, useMemo, useState } from 'react';

import {
  createQColumnHelper,
  DataProvider,
  QAlert,
  QBox,
  QButton,
  QCloseButton,
  QDataTable,
  QDrawer,
  QEmptyState,
  QEmptyStateButtonGroup,
  QEmptyStatePrimaryButton,
  QHeader,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSpinner,
  QStack,
  QText,
  QTitle,
  TableMenuItem,
} from '@qualio/ui-components';
import { useFormik } from 'formik';
import {
  useAuditDeleteMutate,
  useAuditEditMutation,
  useAuditMutation,
  useAuditsQuery,
  useConfigsQuery,
  usePoliciesQuery,
} from 'hooks';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Audit } from 'types';
import { ViewFileCard, CreateAuditForm, NextAudit } from 'v2components';
import { ConfigItem } from 'v2types';
import { useRouterOutletSupplier } from 'v2views';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { getAuditFormObject } from './SupplierAudits.const';

const columnHelper = createQColumnHelper<Audit>();

const SupplierAudits: React.FC = () => {
  const { t } = useTranslation();
  const { supplierId } = useParams<{ supplierId: string }>();
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [showAddAuditModal, setShowAddAuditModal] = useState(false);
  const { supplier } = useRouterOutletSupplier();
  const { isConfigsLoading, auditTypeOptions } = useConfigsQuery();
  const { policyLinks } = usePoliciesQuery(isConfigsLoading);
  const [deleteAuditModalState, setDeleteAuditModalState] = useState<Audit | undefined>(undefined);
  const [editAuditModalState, setEditAuditModalState] = useState<Audit | undefined>(undefined);
  const [addAuditSubmitButtonDisabled, setAddAuditSubmitButtonDisabled] = useState<boolean>(false);
  const [editAuditSubmitButtonDisabled, setEditAuditSubmitButtonDisabled] = useState<boolean>(false);

  const { audits, isAuditsLoading, refetchAudits } = useAuditsQuery(supplierId!);

  const onSuccess = () => {
    setShowAddAuditModal(false);
    formik.resetForm();
    refetchAudits();
  };

  const onDeleteSuccess = () => {
    setDeleteAuditModalState(undefined);
    refetchAudits();
  };

  const onEditSuccess = () => {
    setEditAuditModalState(undefined);
    editFormik.resetForm();
    refetchAudits();
  };

  const { deleteAudit, isAuditDeleteLoading } = useAuditDeleteMutate(onDeleteSuccess);
  const { createAudit, isAuditLoading } = useAuditMutation(onSuccess);
  const { editAudit, isAuditEditLoading } = useAuditEditMutation(onEditSuccess);

  const formik = useFormik<any>({
    initialValues: getAuditFormObject('defaultValue'),
    validationSchema: toFormikValidationSchema(z.object(getAuditFormObject('validation'))),
    validateOnBlur: false,
    onSubmit: (values) => {
      createAudit({ auditState: values, supplierId: supplierId! });
    },
  });

  const editAuditInitialValues = useMemo(() => {
    if (!editAuditModalState) return getAuditFormObject('defaultValue');
    return {
      auditName: editAuditModalState.name || '',
      auditType: editAuditModalState.auditType
        ? { value: editAuditModalState.auditType.id, label: editAuditModalState.auditType.title }
        : '',
      notes: editAuditModalState.notes || '',
      endDate: editAuditModalState.endDate ? new Date(editAuditModalState.endDate) : '',
      documents: editAuditModalState.documents || [],
    };
  }, [editAuditModalState]);

  const editFormik = useFormik<any>({
    initialValues: editAuditInitialValues,
    enableReinitialize: true,
    validationSchema: toFormikValidationSchema(z.object(getAuditFormObject('validation'))),
    validateOnBlur: false,
    onSubmit: (values) => {
      if (editAuditModalState) {
        editAudit({ auditState: values, supplierId: supplierId!, originalAudit: editAuditModalState });
      }
    },
  });

  const handleOnRowClick = useCallback(
    (row: Audit) => {
      setSelectedAudit(audits?.find((item) => item.audit === row.audit) as Audit);
    },
    [audits, setSelectedAudit],
  );

  const baseColumns = useMemo(
    () => [
      columnHelper.text('name', { width: '30%', header: t('supplierAudits.columns.name') }),
      columnHelper.text('auditType.title', { header: t('supplierAudits.columns.auditType') }),
      columnHelper.text('notes', { header: t('supplierAudits.columns.notes') }),
      columnHelper.text('user.fullName', { header: t('supplierAudits.columns.addedBy') }),
      columnHelper.date((audit) => (audit.endDate ? new Date(audit.endDate) : undefined), {
        header: t('supplierAudits.columns.dateCompleted'),
      }),
      columnHelper.date((audit) => (audit.modified ? new Date(audit.modified) : undefined), {
        header: t('supplierAudits.columns.dateAdded'),
      }),
      columnHelper.text((audit) => t('supplierAudits.columns.filesCount', { count: audit.documents?.length ?? 0 }), {
        header: t('supplierAudits.columns.documents'),
      }),
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      ...baseColumns,
      columnHelper.menu({
        items: (
          <>
            <TableMenuItem isDisabled={() => !isAuditEditable(supplier.status)} onClick={setEditAuditModalState}>
              {t('supplierAudits.menu.edit')}
            </TableMenuItem>
            <TableMenuItem onClick={handleOnRowClick}>{t('supplierAudits.menu.viewDocuments')}</TableMenuItem>
            <TableMenuItem isDisabled={() => !isAuditEditable(supplier.status)} onClick={setDeleteAuditModalState}>
              {isAuditDeleteLoading ? <QSpinner /> : t('supplierAudits.menu.delete')}
            </TableMenuItem>
          </>
        ),
      }),
    ],
    [
      baseColumns,
      handleOnRowClick,
      setDeleteAuditModalState,
      setEditAuditModalState,
      isAuditDeleteLoading,
      supplier.status,
      t,
    ],
  );

  if (isAuditsLoading) {
    return (
      <QBox w="100%" textAlign="center" p={5}>
        <QSpinner />
      </QBox>
    );
  }

  return (
    <>
      {!audits?.filter((item) => item.status !== 'ARCHIVED')?.length ? (
        <QEmptyState title={t('supplierAudits.emptyState.title')} subtitle={t('supplierAudits.emptyState.subtitle')}>
          <QEmptyStateButtonGroup>
            <QEmptyStatePrimaryButton>
              <QButton isDisabled={!isAuditEditable(supplier.status)} onClick={() => setShowAddAuditModal(true)}>
                {t('supplierAudits.emptyState.button')}
              </QButton>
            </QEmptyStatePrimaryButton>
          </QEmptyStateButtonGroup>
        </QEmptyState>
      ) : (
        <QBox>
          <NextAudit
            key={supplier.audits?.length}
            audits={audits}
            policyLinks={policyLinks}
            supplierType={supplier?.supplierType as ConfigItem}
            riskLevel={supplier?.riskType as ConfigItem}
            auditTypes={auditTypeOptions as unknown as ConfigItem[]}
            supplier={supplier.supplier}
            isDetails={true}
          />
          <QBox textAlign="right" pb={3}>
            <QButton isDisabled={!isAuditEditable(supplier.status)} onClick={() => setShowAddAuditModal(true)}>
              {t('supplierAudits.buttons.addAudit')}
            </QButton>
          </QBox>
          <DataProvider.Fixed data={audits}>
            <QDataTable columns={columns} />
          </DataProvider.Fixed>
          <QDrawer isOpen={!!selectedAudit} data-cy="audit-documents-drawer">
            <QHeader>
              <QTitle>{selectedAudit?.name ?? ''}</QTitle>
              <QCloseButton onClick={() => setSelectedAudit(null)} />
            </QHeader>
            {selectedAudit?.documents?.length ? (
              <QStack direction="column" spacing={4}>
                {selectedAudit.documents.map((document, index) => (
                  <ViewFileCard key={index} file={document.file} />
                ))}
              </QStack>
            ) : (
              <QAlert status="info" description={t('supplierAudits.drawer.noDocuments')} />
            )}
          </QDrawer>
        </QBox>
      )}
      <QModal isOpen={!!deleteAuditModalState} onClose={() => setDeleteAuditModalState(undefined)}>
        <QModalHeader>
          <QText>{t('supplierAudits.modals.deleteAudit.title')}</QText>
          <QCloseButton onClick={() => setDeleteAuditModalState(undefined)} />
        </QModalHeader>
        <QModalBody>{t('supplierAudits.modals.deleteAudit.message')}</QModalBody>
        <QModalActions>
          <QButton variant="outline" onClick={() => setDeleteAuditModalState(undefined)}>
            {t('supplierAudits.modals.deleteAudit.buttons.cancel')}
          </QButton>
          <QButton
            onClick={() =>
              !!deleteAuditModalState && deleteAudit({ auditState: deleteAuditModalState, supplierId: supplierId! })
            }
            isLoading={isAuditDeleteLoading}
            data-cy="modal-delete-audit-button"
          >
            {t('supplierAudits.modals.deleteAudit.buttons.delete')}
          </QButton>
        </QModalActions>
      </QModal>
      <QModal isOpen={showAddAuditModal} onClose={() => setShowAddAuditModal(false)} size="2xl">
        <QModalHeader>
          <QText>{t('supplierAudits.modals.addAudit.title')}</QText>
          <QCloseButton
            onClick={() => {
              formik.resetForm();
              setShowAddAuditModal(false);
            }}
          />
        </QModalHeader>
        <QModalBody>
          <CreateAuditForm formik={formik} setAddAuditSubmitButtonDisabled={setAddAuditSubmitButtonDisabled} />
        </QModalBody>
        <QModalActions>
          <QButton
            variant="outline"
            onClick={() => {
              formik.resetForm();
              setShowAddAuditModal(false);
            }}
          >
            {t('supplierAudits.modals.addAudit.buttons.cancel')}
          </QButton>
          <QButton
            onClick={() => formik.handleSubmit()}
            isDisabled={addAuditSubmitButtonDisabled}
            isLoading={isAuditLoading}
            data-cy="modal-add-audit-button"
          >
            {t('supplierAudits.modals.addAudit.buttons.add')}
          </QButton>
        </QModalActions>
      </QModal>
      <QModal isOpen={!!editAuditModalState} onClose={() => setEditAuditModalState(undefined)} size="2xl">
        <QModalHeader>
          <QText>{t('supplierAudits.modals.editAudit.title')}</QText>
          <QCloseButton
            onClick={() => {
              editFormik.resetForm();
              setEditAuditModalState(undefined);
            }}
          />
        </QModalHeader>
        <QModalBody>
          <CreateAuditForm formik={editFormik} setAddAuditSubmitButtonDisabled={setEditAuditSubmitButtonDisabled} />
        </QModalBody>
        <QModalActions>
          <QButton
            variant="outline"
            onClick={() => {
              editFormik.resetForm();
              setEditAuditModalState(undefined);
            }}
          >
            {t('supplierAudits.modals.editAudit.buttons.cancel')}
          </QButton>
          <QButton
            onClick={() => editFormik.handleSubmit()}
            isDisabled={editAuditSubmitButtonDisabled}
            isLoading={isAuditEditLoading}
            data-cy="modal-edit-audit-button"
          >
            {t('supplierAudits.modals.editAudit.buttons.save')}
          </QButton>
        </QModalActions>
      </QModal>
    </>
  );
};

export default SupplierAudits;

const isAuditEditable = (status: any) => {
  // Adding audits are not allowed when supplier is in pending review status
  return status !== 'REVIEW_PENDING';
};
